import SuboptimalState from '@components/SuboptimalState/SuboptimalState';
import { getChannel } from '@components/SubscriptionGroups/utils';
import { useGetSubscriptionGroupById } from '@hooks/subscriptionGroups/useSubscriptionGroups';
import {
    useUnsubscribeGroup,
    useUserSubscriptionPreference,
} from '@hooks/subscriptionGroups/useSubscriptionPreference';
import useNotify from '@hooks/toaster/useNotify';
import { useLocale } from '@hooks/useLocale';
import { useTracking } from '@hooks/useTracking';
import {
    type SubscriptionPreferenceCreateOrUpdateRequest,
    UnsubUrlQueryParam,
    decodeSubToken,
} from '@lightdash/common';
import { Box, Button, Flex, Loader, Text } from '@mantine/core';
import React, { useCallback, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router';
import { EventName } from 'types/Events';
import TestModeUnsub from './TestModeUnsub';

const UnsubscribeGroup: React.FC = () => {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);

    const subToken = searchParams.get('subToken') || '';
    const parsedSubToken = decodeSubToken(subToken);

    const campaign =
        (parsedSubToken?.campaignId ?? searchParams.get('campaign')) || '';
    const channel =
        (parsedSubToken?.channel ?? searchParams.get('channel')) || '';
    const subGroupId =
        (parsedSubToken?.subGroupId ?? searchParams.get('subGroupId')) || '';

    const userId = (parsedSubToken?.userId ?? searchParams.get('userId')) || '';
    const projectId =
        (parsedSubToken?.projectId ?? searchParams.get('projectId')) || '';
    const mode = searchParams.get('mode') || '';
    const channelIdentifier =
        parsedSubToken?.channelIdentifier ??
        searchParams.get('channelIdentifier');

    const { t } = useLocale();
    const { showToastError } = useNotify();

    const {
        data: groupData,
        isLoading: isSubscriptionGroupLoading,
        isError: isSubscriptionGroupError,
    } = useGetSubscriptionGroupById({
        groupId: subGroupId,
        projectId: projectId,
    });

    const {
        data: userSubscriptionPreference,
        isFetching: isUserPreferenceFetching,
        isError: isUserPreferenceError,
    } = useUserSubscriptionPreference({
        channel: getChannel(channel),
        userId: userId,
        subGroupId: subGroupId,
        channelIdentifier: channelIdentifier ?? '',
    });

    const {
        mutateAsync: unsubscribeGroup,
        isLoading: isUnsubscribeLoading,
        isSuccess: isUnsubscribeSuccess,
    } = useUnsubscribeGroup();

    const { track } = useTracking();

    useEffect(() => {
        if (isSubscriptionGroupError) {
            showToastError({
                title: t('subscription_group.unsubscribe_group_api_error'),
            });
        }
    }, [isSubscriptionGroupError, showToastError, t]);

    useEffect(() => {
        if (isUserPreferenceError) {
            showToastError({
                title: t(
                    'subscription_group.unsubscribe_group_user_preference_api_error',
                ),
            });
        }
    }, [isUserPreferenceError, showToastError, t]);

    const preferenceUrl = useMemo(() => {
        const baseUrl = '/subscription-preferences/channel';
        let queryParams;
        if (subToken && parsedSubToken) {
            queryParams = new URLSearchParams({
                subToken: subToken,
            });
        } else {
            queryParams = new URLSearchParams({
                channel: channel ?? '',
                userId: userId ?? '',
                projectId: projectId ?? '',
                ...(channelIdentifier && {
                    channelIdentifier: channelIdentifier,
                }),
            });
        }

        if (mode === UnsubUrlQueryParam.TEST) {
            queryParams.set('mode', UnsubUrlQueryParam.TEST);
        }

        return `${baseUrl}?${queryParams.toString()}`;
    }, [
        subToken,
        parsedSubToken,
        mode,
        channel,
        userId,
        projectId,
        channelIdentifier,
    ]);

    const handleUnsubscribe = useCallback(async () => {
        let payload: SubscriptionPreferenceCreateOrUpdateRequest = {
            userId: userId,
            subscriptionGroupId: subGroupId,
            actionTaken: false,
            channel: getChannel(channel),
            campaignId: campaign,
        };

        if (channelIdentifier) {
            payload = {
                ...payload,
                channelId: channelIdentifier,
            };
        }

        track(
            {
                name: EventName.UNSUBSCRIBE_BUTTON_CLICKED,
                payload,
            },
            projectId,
        );

        try {
            await unsubscribeGroup({
                payload,
            });
        } catch (error) {
            showToastError({
                title: t('subscription_preference.unsubscribe_error'),
            });
        }
    }, [
        userId,
        subGroupId,
        channel,
        unsubscribeGroup,
        campaign,
        channelIdentifier,
        track,
        projectId,
        showToastError,
        t,
    ]);

    if (isUserPreferenceFetching || isSubscriptionGroupLoading) {
        return (
            <Box className="flex justify-center items-center px-4 w-screen h-screen">
                <Flex className="p-3.5" align="center">
                    <Loader size={11} />
                    <Text className="text-sm font-normal text-blu-800 px-1.5">
                        {t('subscription_body.loading')}
                    </Text>
                </Flex>
            </Box>
        );
    }

    //Info: If any of the Group or User Preference API errors, show the invalid link error
    if (isSubscriptionGroupError || isUserPreferenceError) {
        return (
            <Box className="flex flex-col justify-center items-center px-4 w-screen h-screen">
                <SuboptimalState title={t('common.invalid_urllink_error')} />
            </Box>
        );
    }

    if (!userSubscriptionPreference || isUnsubscribeSuccess) {
        return (
            <Box className="flex flex-col justify-center items-center px-4 w-screen h-screen">
                <Box>
                    <Text className="text-gray-800 font-medium text-lg mb-3.5">
                        {t('unsubscribe_group.user_unsubscribed_successfully', {
                            groupName: groupData ? groupData.name : '',
                        })}
                    </Text>
                    <Text className="mt-3 mb-2 max-w-xl text-sm font-medium text-gray-600">
                        {t('subscription_preference.unsubscribe_all')}
                    </Text>
                    <Text className="text-sm font-medium text-gray-600">
                        {t(
                            'unsubscribe_group.manage_all_your_subscription_preferences',
                        )}{' '}
                        <Link
                            className="underline hover:cursor-pointer"
                            to={preferenceUrl}
                        >
                            {t('unsubscribe_group.here')}{' '}
                        </Link>
                    </Text>
                </Box>
            </Box>
        );
    }

    return (
        <Box className="flex flex-col justify-center items-center px-4 w-screen h-screen">
            <Box className="w-full lg:w-2/5">
                {mode === UnsubUrlQueryParam.TEST && <TestModeUnsub />}
                <Text className="mb-3 text-lg font-medium text-gray-800">
                    {t(
                        'unsubscribe_group.do_you_want_to_unsubscribe_from_these_kinds_of',
                        {
                            org: '',
                            channel: channel,
                        },
                    )}
                </Text>
                <Text className="text-gray-600 text-sm font-medium mb-3.5">
                    {t('unsubscribe_group.subtitle', {
                        channel: channel,
                        groupName: groupData ? groupData.name : '',
                    })}
                </Text>

                <Button
                    onClick={() => handleUnsubscribe()}
                    className="mb-1.5 bg-black px-3 py-1.5 text-sm font-medium text-white hover:!bg-black  "
                    loading={isUnsubscribeLoading}
                >
                    {t('unsubscribe_group.unsubscribe')}
                </Button>

                <Text className="text-sm font-medium text-gray-600">
                    {t(
                        'unsubscribe_group.manage_all_your_subscription_preferences',
                    )}{' '}
                    <Link
                        className="underline hover:cursor-pointer"
                        to={preferenceUrl}
                    >
                        {t('unsubscribe_group.here')}{' '}
                    </Link>
                </Text>
            </Box>
        </Box>
    );
};

export default UnsubscribeGroup;
